import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Result, Spin, Typography } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';

const { Text } = Typography;

interface UnsubscribePageProps {}

const UnsubscribePage: React.FC<UnsubscribePageProps> = () => {
  const { token, messageTypeCode } = useParams<{ token: string; messageTypeCode?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    console.log('UnsubscribePage mounted, user:', user, 'isAuthenticated:', isAuthenticated);

    // If user is logged in, redirect immediately to preferences
    if (isAuthenticated) {
      console.log('User is authenticated, redirecting immediately');
      navigate('/dashboard/profile?tab=messages&openPreferences=true', { replace: true });
      return;
    }

    const unsubscribe = async () => {
      if (!token) {
        setError('Invalid unsubscribe link');
        setLoading(false);
        return;
      }

      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') || 'http://localhost:3501';
        const endpoint = messageTypeCode
          ? `${apiBaseUrl}/messages/unsubscribe/${token}/${messageTypeCode}`
          : `${apiBaseUrl}/messages/unsubscribe/${token}`;

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });

        if (response.ok || response.status === 304) {
          // 304 means the request was cached, but unsubscribe likely already succeeded
          setSuccess(true);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || 'Failed to unsubscribe. Please try again later.');
        }
      } catch (err: any) {
        setError('Failed to unsubscribe. Please try again later.');
      } finally {
        setLoading(false);
        // Start countdown after the request completes
        setCountdown(7);
      }
    };

    unsubscribe();
  }, [token, messageTypeCode, isAuthenticated, navigate, user]);

  // Countdown timer and redirect effect
  useEffect(() => {
    console.log('Countdown effect triggered, countdown:', countdown);
    if (countdown === null) return;

    if (countdown === 0) {
      if (isAuthenticated) {
        // Always go to preferences when logged in
        const targetUrl = '/dashboard/profile?tab=messages&openPreferences=true';
        console.log('Redirecting to:', targetUrl);
        navigate(targetUrl, { replace: true });
      } else {
        // If not logged in, go to homepage
        navigate('/');
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, navigate, isAuthenticated]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px'
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Processing your unsubscribe request...</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f5f5f5'
    }}>
      <Card style={{ maxWidth: 500, width: '100%' }}>
        <Result
          icon={error ? <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> : <CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title={error ? 'Unsubscribe Failed' : isAuthenticated ? 'Manage Your Preferences' : 'Unsubscribed Successfully'}
          subTitle={
            <div>
              {error ? (
                <Text>{error}</Text>
              ) : isAuthenticated ? (
                <Text>
                  You're logged in! We'll take you to your message preferences where you can manage all your notification settings.
                </Text>
              ) : success && messageTypeCode ? (
                <Text>
                  You have been unsubscribed from {messageTypeCode.replace(/_/g, ' ')} notifications.
                </Text>
              ) : success ? (
                <Text>
                  You have been unsubscribed from all email notifications.
                </Text>
              ) : null}
            </div>
          }
          extra={
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                You can update your notification preferences anytime by logging into your account.
              </Text>
              {countdown !== null && (
                <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '6px' }}>
                  <Text style={{ fontSize: '14px' }}>
                    {isAuthenticated
                      ? `Taking you to your message preferences in ${countdown} seconds...`
                      : `Redirecting to homepage in ${countdown} seconds...`
                    }
                  </Text>
                </div>
              )}
            </div>
          }
        />
      </Card>
    </div>
  );
};

export default UnsubscribePage;